/**
 * The connection gates of the app areas that need one. The answer itself is
 * pure (appGate); this module reads each gated app's connection and keeps
 * its last view, so the layout, the area and the launcher agree.
 *
 * Gating an app: a `landing` on its definition (appRegistry), an entry in
 * APP_AREA_GATES, and one line in useAppAreaConnection reading its status.
 *
 * @module components/apps/appAreaGates
 */
import { useEffect } from "react";

import {
  appAreaView,
  appLastViewKey,
  connectionFromStatus,
  resumableAppHref,
  type AppAreaView,
  type AppConnection,
} from "./appGate";
import { appLanding } from "./appRegistry";
import { appAreaFromPathname, type AppAreaId } from "./appsPaths";
import { imanageSection } from "./imanage/imanagePaths";
import { useIManageStatusQuery } from "./imanage/imanageQueries";

interface AppAreaGate {
  /** Pages that stay reachable while disconnected (where the app is set up). */
  readonly isExempt: (pathname: string) => boolean;
  /** Pages that are never reopened as the app's last view. */
  readonly isTransient: (pathname: string) => boolean;
}

const APP_AREA_GATES: Partial<Readonly<Record<AppAreaId, AppAreaGate>>> = {
  imanage: {
    isExempt: (pathname) => imanageSection(pathname)?.kind === "settings",
    isTransient: (pathname) => {
      const kind = imanageSection(pathname)?.kind;
      return kind === "settings" || kind === "about" || kind === "other";
    },
  },
};

const isGated = (area: AppAreaId): boolean =>
  APP_AREA_GATES[area] !== undefined && appLanding(area) !== null;

/**
 * The person's connection to the gated app `area` is, or "connected" for
 * anything else. Every gate's read runs on every render (in this order), and
 * reads nothing unless its area is the one asked for.
 */
function useAppAreaConnection(area: AppAreaId | null, organizationId: string | null): AppConnection {
  const imanageOrg = area === "imanage" ? organizationId : null;
  const imanage = connectionFromStatus(useIManageStatusQuery(imanageOrg), imanageOrg !== null);
  return area === "imanage" ? imanage : "connected";
}

/** Which face the app area under `pathname` shows; null outside an app area. */
export function useAppAreaView(pathname: string, organizationId: string | null): AppAreaView | null {
  const area = appAreaFromPathname(pathname);
  const connection = useAppAreaConnection(area, organizationId);
  if (!area) return null;
  return appAreaView({
    gated: isGated(area),
    connection,
    exempt: APP_AREA_GATES[area]?.isExempt(pathname) ?? false,
  });
}

/** Remembers the page a gated app shows, so opening the app again resumes there. */
export function useRememberAppView(
  area: AppAreaId,
  orgSegment: string,
  view: AppAreaView,
  location: { readonly pathname: string; readonly href: string },
): void {
  const { pathname, href } = location;
  useEffect(() => {
    const gate = APP_AREA_GATES[area];
    if (view !== "app" || !gate || gate.isTransient(pathname)) return;
    if (!resumableAppHref(href, orgSegment, area)) return;
    try {
      localStorage.setItem(appLastViewKey(orgSegment, area), href);
    } catch {
      // Storage is a convenience; the app opens on its default page without it.
    }
  }, [area, orgSegment, view, pathname, href]);
}

/** Where opening a gated app resumes (its last view), or null for its default page. */
export function readAppResumeHref(orgSegment: string, appId: string): string | null {
  if (!(appId in APP_AREA_GATES)) return null;
  try {
    return resumableAppHref(localStorage.getItem(appLastViewKey(orgSegment, appId)), orgSegment, appId);
  } catch {
    return null;
  }
}
